import { z } from 'zod';

/**
 * Validates Vite environment variables at application startup.
 * Fails fast in development when required variables are malformed.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.string().default(''),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_FEATURE_AI_INSIGHTS: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_FEATURE_NAMED_CREDENTIALS: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
  VITE_FEATURE_PLATFORM_ADMIN: z
    .string()
    .default('true')
    .transform((v) => v === 'true'),
});

export type AppEnv = z.infer<typeof envSchema>;

export function validateEnv(): AppEnv {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    console.error('Invalid environment configuration:', result.error.flatten().fieldErrors);
    throw new Error('Environment validation failed. Check .env.local');
  }

  return result.data;
}

/** Validated environment singleton — initialized in app bootstrap. */
let _env: AppEnv | null = null;

export function getEnv(): AppEnv {
  if (!_env) {
    _env = validateEnv();
  }
  return _env;
}

export function isDevelopment(): boolean {
  return getEnv().VITE_APP_ENV === 'development';
}

export function isProduction(): boolean {
  return getEnv().VITE_APP_ENV === 'production';
}
